import { useRef, useState, type DragEvent } from 'react';
import { DEMO_CSV, DEMO_JSON, DEMO_NDJSON } from '../lib/demoData';

interface DropZoneProps {
  busy: boolean;
  activeFileName: string | null;
  onLoad: (text: string, fileName: string) => void;
  onError: (message: string) => void;
}

const MAX_INLINE = 15 * 1024 * 1024;

export function DropZone({ busy, activeFileName, onLoad, onError }: DropZoneProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const readFile = async (file: File) => {
    if (file.size > MAX_INLINE) {
      onError(`“${file.name}” is ${(file.size / 1048576).toFixed(1)} MB — over the 15 MB inline limit.`);
      return;
    }
    try {
      const text = await file.text();
      if (text.trim() === '') {
        onError(`“${file.name}” is empty.`);
        return;
      }
      onLoad(text, file.name);
    } catch {
      onError(`Could not read “${file.name}”.`);
    }
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files?.[0];
    if (file) void readFile(file);
  };

  return (
    <section
      onDragOver={(event) => {
        event.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      className={`rounded-2xl border-2 border-dashed p-5 text-center transition-colors sm:p-6 ${
        dragOver ? 'border-amber-500 bg-amber-500/5' : 'border-slate-800 bg-slate-900/30'
      }`}
    >
      <p className="text-sm text-slate-300">
        Drop a <span className="font-mono text-amber-400">.csv</span> /{' '}
        <span className="font-mono text-lime-400">.json</span> /{' '}
        <span className="font-mono text-sky-400">.ndjson</span> file here
        {activeFileName && <span className="text-slate-500"> — currently: {activeFileName}</span>}
      </p>
      <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
          className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-white disabled:opacity-40"
        >
          {busy ? 'Working…' : 'Choose file'}
        </button>
        <span className="text-[11px] text-slate-600">or try a demo:</span>
        <button
          type="button"
          disabled={busy}
          onClick={() => onLoad(DEMO_CSV, 'demo-messy.csv')}
          className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-slate-300 transition hover:border-slate-500 hover:text-white disabled:opacity-40"
        >
          messy CSV
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onLoad(DEMO_JSON, 'demo-orders.json')}
          className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-slate-300 transition hover:border-slate-500 hover:text-white disabled:opacity-40"
        >
          nested JSON
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onLoad(DEMO_NDJSON, 'demo-hostile.ndjson')}
          className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-slate-300 transition hover:border-slate-500 hover:text-white disabled:opacity-40"
        >
          hostile NDJSON
        </button>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept=".csv,.json,.ndjson,.tsv,.txt,text/csv,application/json"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void readFile(file);
          event.target.value = '';
        }}
      />
      <p className="mt-3 text-[11px] text-slate-600">
        100% local — parsed in a Web Worker in this tab, never uploaded.
      </p>
    </section>
  );
}
